import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@ui/card.tsx';
import {
  useSegmentsForEntity,
  useApiAdminsegmentsSegmentIdentitiesPost,
  useApiAdminsegmentsSegmentIdentitiesIdDelete,
  getSegmentsForEntityQueryKey
} from '@r/api/generated/client.ts';
import type { SegmentEntitySegmentEntityWriteModelType } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { SegmentPicker, type PickedSegment } from '@admin/components/segments/segment-picker.tsx';

interface EntitySegmentsCardProps {
  entityType: SegmentEntitySegmentEntityWriteModelType;
  entityId: number;
}

/**
 * Carte de "conditionnement d'affichage" : permet d'attacher/détacher des segments à une
 * entité segmentable (shop, widget, game) pour restreindre sa visibilité aux membres correspondants.
 * Chaque ajout/retrait déclenche immédiatement l'appel API correspondant. Les textes restent
 * génériques (mêmes clés de trad) quel que soit le type d'entité segmentable.
 */
export const EntitySegmentsCard: FC<EntitySegmentsCardProps> = ({ entityType, entityId }) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const attachedQuery = useSegmentsForEntity(entityType, entityId);

  const attachedEntities = attachedQuery.data ?? [];
  const attachedSegments: PickedSegment[] = attachedEntities.flatMap((entity) =>
    entity.segment ? [{ id: entity.segment.id, name: entity.segment.name }] : []
  );

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getSegmentsForEntityQueryKey(entityType, entityId)
    });
  };

  const attachMutation = useApiAdminsegmentsSegmentIdentitiesPost({
    mutation: {
      onSuccess: invalidate,
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'post', '/admin/segments')) {
          showOrvalErrorToast(error, t('admin.segments.conditioning.attach.error'));
        } else {
          toast.error(t('admin.segments.conditioning.attach.error'));
        }
      }
    }
  });

  const detachMutation = useApiAdminsegmentsSegmentIdentitiesIdDelete({
    mutation: {
      onSuccess: invalidate,
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/segments')) {
          showOrvalErrorToast(error, t('admin.segments.conditioning.detach.error'));
        } else {
          toast.error(t('admin.segments.conditioning.detach.error'));
        }
      }
    }
  });

  const handleAttach = (segment: PickedSegment) => {
    attachMutation.mutate({ segmentId: segment.id, data: { entityId, type: entityType } });
  };

  const handleDetach = (segment: PickedSegment) => {
    const segmentEntity = attachedEntities.find((entity) => entity.segment?.id === segment.id);
    if (!segmentEntity) return;
    detachMutation.mutate({ segmentId: segment.id, id: segmentEntity.id });
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold">{t('admin.segments.conditioning.title')}</h2>
          <p className="text-muted-foreground">{t('admin.segments.conditioning.description')}</p>
        </div>

        <SegmentPicker
          selectedSegments={attachedSegments}
          onSelect={handleAttach}
          onRemove={handleDetach}
        />
      </CardContent>
    </Card>
  );
};
